/* ============================================================================
   ChipMultiSelect — a compact chip-based multi-select used across the intake
   form (objectives, success metrics, channels, deliverable formats). Toggling a
   chip adds/removes its code from the selected set. Uses the shared .chip class.
   ============================================================================ */

import type { LookupOption } from './data';

interface Props {
  label: string;
  hint?: string;
  options: LookupOption[];
  selected: string[];
  onChange: (next: string[]) => void;
}

export default function ChipMultiSelect({ label, hint, options, selected, onChange }: Props) {
  function toggle(code: string): void {
    if (selected.includes(code)) onChange(selected.filter((c) => c !== code));
    else onChange([...selected, code]);
  }

  return (
    <div className="field">
      <label>{label}</label>
      {hint && <span className="hint">{hint}</span>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 2 }}>
        {options.length === 0 ? (
          <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
            No options available.
          </span>
        ) : (
          options.map((o) => {
            const on = selected.includes(o.code);
            return (
              <button
                key={o.code}
                type="button"
                className={`chip ${on ? 'chip-active' : ''}`}
                aria-pressed={on}
                onClick={() => toggle(o.code)}
                style={{ cursor: 'pointer' }}
              >
                {on && <span aria-hidden>✓</span>}
                {o.name}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
